import {
  CommunityPost,
  CommunityReply,
  useCreateReply,
  useDeletePost,
  useDeleteReply,
  useToggleLikePost,
  useUpdatePost,
} from '@api/communityPostsApiHandler';
import ChatCircle from '@assets/images/icons/chat-circle.svg';
import PersonIcon from '@assets/images/icons/person.svg';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Skeleton } from '@mui/material';
import { MotionImage, MotionText, showToast } from '@shared/atoms';
import { SystemButton } from '@shared/molecules/SystemButton/SystemButton';
import { formatDateToDayShortMonthYear } from '@utils/date.util';
import { ToastMessages } from 'constants/toastMessages';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { ReplyCard } from '../ReplyCard/ReplyCard';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import styles from './index.module.scss';

interface PostCardProps {
  post: CommunityPost;
  isExpanded: boolean;
  onToggleExpand: () => void;
  replies: CommunityReply[];
  loadingReplies: boolean;
  refetchReplies: (postId: number) => void;
  onInteractionChange?: (isInteracting: boolean) => void;
  /** Called after the post is successfully deleted (e.g. leave dedicated post page). */
  onPostDeleted?: () => void;
}

type DeleteType = 'post' | 'reply';

interface DeleteState {
  show: boolean;
  type: DeleteType;
  id: number;
  onSuccess?: () => void;
  onError?: () => void;
}

export const PostCard: React.FC<PostCardProps> = ({
  post: initialPost,
  isExpanded,
  onToggleExpand,
  replies,
  loadingReplies,
  refetchReplies,
  onInteractionChange,
  onPostDeleted,
}) => {
  const [post, setPost] = useState(initialPost);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [deleteState, setDeleteState] = useState<DeleteState>({
    show: false,
    type: 'post',
    id: 0,
  });
  const [showConfetti, setShowConfetti] = useState(false);

  const updatePost = useUpdatePost();
  const deletePost = useDeletePost();
  const deleteReply = useDeleteReply();
  const createReply = useCreateReply();
  const toggleLike = useToggleLikePost();

  // Update local post state when prop changes
  useEffect(() => {
    setPost(initialPost);
  }, [initialPost]);

  const handleLike = useCallback(() => {
    const wasLiked = post.isLiked;

    // Optimistic update
    setPost((prev) => ({
      ...prev,
      isLiked: !prev.isLiked,
      likesCount: prev.isLiked ? prev.likesCount - 1 : prev.likesCount + 1,
    }));

    // Show confetti animation when liking
    if (!wasLiked) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1000);
    }

    toggleLike.mutate(post.id, {
      onSuccess: (response) => {
        // Update with server response
        setPost((prev) => ({
          ...prev,
          isLiked: response.responseObject?.isLiked ?? prev.isLiked,
          likesCount: response.responseObject?.likesCount ?? prev.likesCount,
        }));
      },
      onError: () => {
        // Revert on error
        setPost(initialPost);
      },
    });
  }, [post.id, post.isLiked, toggleLike, initialPost]);

  const handleReplyClick = useCallback(() => {
    setShowReplyInput(true);
    if (!isExpanded) {
      onToggleExpand();
    }
  }, [isExpanded, onToggleExpand]);

  const handleCancelReply = useCallback(() => {
    setShowReplyInput(false);
    setReplyContent('');
  }, []);

  const handleSubmitReply = useCallback(() => {
    if (!replyContent.trim() || replyContent.length > 5000) {
      return;
    }

    createReply.mutate(
      { postId: post.id, content: replyContent },
      {
        onSuccess: (response) => {
          setReplyContent('');
          setShowReplyInput(false);
          // Update local reply count optimistically
          setPost((prev) => ({
            ...prev,
            repliesCount: prev.repliesCount + 1,
          }));
          const messageKey =
            response.responseObject?.status === 'active'
              ? ToastMessages.communityFeed.success.REPLY_CREATED_SUCCESSFULLY
              : ToastMessages.communityFeed.success.REPLY_CREATED_SUCCESSFULLY_AND_ON_REVIEW;
          showToast(messageKey, undefined, 'success');
        },
        onError: () => {
          showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
        },
      },
    );
  }, [replyContent, post.id, createReply]);

  const handleEdit = useCallback(() => {
    setEditContent(post.content);
    setIsEditing(true);
  }, [post.content]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditContent('');
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editContent.trim() || editContent.length > 5000) {
      return;
    }

    updatePost.mutate(
      { postId: post.id, content: editContent },
      {
        onSuccess: (response) => {
          setPost(response.responseObject || post);
          setIsEditing(false);
          setEditContent('');
          showToast(
            ToastMessages.communityFeed.success.YOUR_POST_UPDATED_SUCCSSFULLY,
            undefined,
            'success',
          );
        },
        onError: () => {
          showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
        },
      },
    );
  }, [editContent, post, updatePost]);

  const handleDeletePost = useCallback(() => {
    setDeleteState({
      show: true,
      type: 'post',
      id: post.id,
    });
  }, [post.id]);

  const handleReplyDeleteRequest = useCallback(
    (replyId: number, onSuccess: () => void, onError: () => void) => {
      setDeleteState({
        show: true,
        type: 'reply',
        id: replyId,
        onSuccess,
        onError,
      });
    },
    [],
  );

  const handleConfirmDelete = useCallback(() => {
    if (deleteState.type === 'post') {
      deletePost.mutate(deleteState.id, {
        onSuccess: () => {
          setDeleteState({ show: false, type: 'post', id: 0 });
          showToast(
            ToastMessages.communityFeed.success.YOUR_POST_REMOVED_SUCCSSFULLY,
            undefined,
            'success',
          );
          onPostDeleted?.();
        },
        onError: () => {
          showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
          setDeleteState({ show: false, type: 'post', id: 0 });
        },
      });
    } else {
      deleteReply.mutate(deleteState.id, {
        onSuccess: () => {
          setDeleteState({ show: false, type: 'reply', id: 0 });
          if (deleteState.onSuccess) {
            deleteState.onSuccess();
          }
        },
        onError: () => {
          setDeleteState({ show: false, type: 'reply', id: 0 });
          if (deleteState.onError) {
            deleteState.onError();
          }
        },
      });
    }
  }, [deleteState, deletePost, deleteReply, onPostDeleted]);

  const handleCancelDelete = useCallback(() => {
    setDeleteState({ show: false, type: 'post', id: 0 });
  }, []);

  const refetchPostReplies = useCallback(() => {
    if (post) {
      refetchReplies(post?.id);
    }
  }, [post]);

  const isDeleting = deleteState.type === 'post' ? deletePost.isPending : deleteReply.isPending;

  // Notify parent (carousel) when user is interacting with the post
  useEffect(() => {
    if (!onInteractionChange) {
      return;
    }

    const isInteracting = isEditing || deleteState.show || showReplyInput || isExpanded;
    onInteractionChange(isInteracting);
  }, [isEditing, deleteState.show, showReplyInput, isExpanded, onInteractionChange]);

  return (
    <>
      <motion.div
        className={styles.postCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Post Header */}
        <div className={styles.postHeader}>
          <div className={styles.postAvatar}>
            <MotionImage src={PersonIcon} alt="Chat Icon" className={styles.chatIcon} />
          </div>
          <div className={styles.postHeaderInfo}>
            <MotionText as="h3" text={post.postedBy} className={styles.postAuthor} />
            <MotionText
              as="text"
              className={styles.postDate}
              text={`Posted on ${formatDateToDayShortMonthYear(post?.timestamp)}`}
            />
          </div>
          {post.isOwner && !isEditing && (
            <div className={styles.postActions}>
              <motion.button
                className={styles.editBtn}
                onClick={handleEdit}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <EditIcon fontSize="small" />
              </motion.button>
              <motion.button
                className={styles.deleteBtn}
                onClick={handleDeletePost}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </motion.button>
            </div>
          )}
        </div>

        {/* Post Content - Editable or Static */}
        {isEditing ? (
          <div className={styles.editPostContainer}>
            <textarea
              className={styles.editTextarea}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              maxLength={5000}
              rows={4}
            />
            <div className={styles.editActions}>
              <MotionText
                as="text"
                text={`${editContent.length}/5000`}
                className={styles.charCount}
              />
              <div className={styles.editButtons}>
                <SystemButton
                  variant="outlinedPrimarySkyBlue"
                  onClick={handleCancelEdit}
                  size="small"
                  disabled={updatePost.isPending}
                >
                  Cancel
                </SystemButton>
                <SystemButton
                  variant="primaryDarkSkyBlue"
                  onClick={handleSaveEdit}
                  size="small"
                  disabled={!editContent.trim() || updatePost.isPending}
                >
                  {updatePost.isPending ? 'Saving...' : 'Save'}
                </SystemButton>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.postContent}>
            <MotionText as="text" text={post.content} />
          </div>
        )}

        {/* Post Footer */}
        {!isEditing && (
          <div className={styles.postFooter}>
            <LikeButton
              isLiked={post.isLiked}
              likesCount={post?.likesCount || 0}
              onLike={handleLike}
              disabled={toggleLike.isPending}
              showConfetti={showConfetti}
            />

            <motion.button
              className={styles.replyBtn}
              onClick={onToggleExpand}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MotionImage src={ChatCircle} alt="" className={styles.chatImage} />
              <MotionText
                className={styles.text}
                as="span"
                text={`${post?.repliesCount ? post.repliesCount : 0} ${
                  post.repliesCount === 1 ? 'Reply' : 'Replies'
                }`}
              />
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </motion.button>

            <SystemButton
              className={styles.replyActionBtn}
              onClick={handleReplyClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              variant="outlinedPrimarySkyBlue"
            >
              Reply
            </SystemButton>
          </div>
        )}

        {/* Replies Section */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              className={styles.repliesSection}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Reply Input */}
              <AnimatePresence>
                {showReplyInput && (
                  <motion.div
                    className={styles.replyInputContainer}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <textarea
                      className={styles.replyTextarea}
                      placeholder="Write your reply..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      rows={3}
                      maxLength={5000}
                    />
                    <div className={styles.replyInputActions}>
                      <MotionText
                        as="text"
                        text={`${replyContent.length}/5000`}
                        className={styles.charCount}
                      />
                      <div className={styles.replyButtons}>
                        <SystemButton
                          variant="outlinedPrimarySkyBlue"
                          className={styles.cancelReplyBtn}
                          onClick={handleCancelReply}
                          size="small"
                          disabled={createReply.isPending}
                        >
                          Cancel
                        </SystemButton>
                        <SystemButton
                          className={styles.submitReplyBtn}
                          onClick={handleSubmitReply}
                          disabled={!replyContent.trim() || createReply.isPending}
                          size="small"
                          variant="primaryDarkSkyBlue"
                        >
                          {createReply.isPending ? 'Submitting...' : 'Submit'}
                        </SystemButton>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Replies List */}
              {loadingReplies ? (
                <div className={styles.repliesLoading}>
                  {[1, 2].map((index) => (
                    <div key={index} className={styles.replyCard}>
                      <div className={styles.replyHeader}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <div className={styles.replyHeaderInfo}>
                          <Skeleton variant="text" width={120} height={20} />
                          <Skeleton variant="text" width={80} height={16} />
                        </div>
                      </div>
                      <Skeleton variant="rectangular" height={40} />
                    </div>
                  ))}
                </div>
              ) : replies.length === 0 ? (
                <div className={styles.noReplies}>
                  <MotionText as="text" text="No replies yet. Be the first to reply!" />
                </div>
              ) : (
                <div className={styles.repliesList}>
                  {replies.map((reply) => (
                    <ReplyCard
                      key={reply.id}
                      reply={reply}
                      onDeleteRequest={handleReplyDeleteRequest}
                      refetchReplies={refetchPostReplies}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Centralized Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={deleteState.show}
        title={deleteState.type === 'post' ? 'Delete Post?' : 'Delete Reply?'}
        message={`Are you sure you want to delete this ${deleteState.type}? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};

PostCard.defaultProps = {
  onInteractionChange: undefined,
  onPostDeleted: undefined,
};

// Like Button Component with Confetti
export const LikeButton: React.FC<{
  isLiked: boolean;
  likesCount: number;
  onLike: () => void;
  disabled: boolean;
  showConfetti: boolean;
}> = ({ isLiked, likesCount, onLike, disabled, showConfetti }) => {
  const confettiColors = ['#019df2', '#beaaf3', '#fcd300', '#8eea36', '#06bf97', '#ff8a4a'];

  return (
    <div className={styles.likeButtonWrapper}>
      <motion.button
        className={styles.likeBtn}
        onClick={onLike}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        disabled={disabled}
      >
        {isLiked ? (
          <FavoriteIcon
            fontSize="medium"
            style={{
              color: '#019df2',
              fontSize: '30px',
            }}
          />
        ) : (
          <FavoriteBorderIcon
            fontSize="medium"
            style={{
              color: '#0072b0',
              fontSize: '30px',
            }}
          />
        )}
        <span>{likesCount}</span>
      </motion.button>
      {showConfetti && (
        <div className={styles.confettiContainer}>
          {confettiColors.map((color, index) => (
            <ConfettiParticle key={index} color={color} delay={index * 0.03} index={index} />
          ))}
          {confettiColors.map((color, index) => (
            <ConfettiParticle
              key={`second-${index}`}
              color={color}
              delay={0.1 + index * 0.03}
              index={index + 6}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Confetti particle component
const ConfettiParticle: React.FC<{ color: string; delay: number; index: number }> = ({
  color,
  delay,
  index,
}) => {
  const angle = (index * 360) / 12;
  const distance = 80 + Math.random() * 20;
  const x = Math.cos((angle * Math.PI) / 180) * distance;
  const y = Math.sin((angle * Math.PI) / 180) * distance;

  return (
    <motion.div
      className={styles.confettiParticle}
      style={{ backgroundColor: color }}
      initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
      animate={{
        scale: [0, 1, 0.8, 0],
        x: [0, x * 0.5, x, x * 1.2],
        y: [0, y * 0.5, y, y * 1.2 + 20],
        opacity: [1, 1, 0.8, 0],
        rotate: Math.random() * 360,
      }}
      transition={{
        duration: 1.5,
        delay,
        ease: 'easeOut',
      }}
    />
  );
};
